/**
 * Diálogo modal y confirmación.
 *
 * Se usa `<dialog>` nativo por lo que trae resuelto: foco atrapado, cierre con
 * Escape y capa de fondo sin z-index inventados.
 *
 * `Confirmacion` es obligatoria antes de cualquier acción irreversible. El §5
 * lo pide explícitamente para el borrado duro, y la fusión, aunque reversible
 * en la base, no tiene botón de deshacer en la interfaz.
 */

import { AlertTriangle, X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'

import { Boton } from './primitivos'
import { cx } from './util'

export function Dialogo({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  children,
  pie,
  ancho = 'md',
}: {
  abierto: boolean
  onCerrar: () => void
  titulo: string
  descripcion?: string
  children?: ReactNode
  pie?: ReactNode
  ancho?: 'sm' | 'md' | 'lg'
}) {
  const dialogo = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const elemento = dialogo.current
    if (!elemento) return
    if (abierto && !elemento.open) elemento.showModal()
    if (!abierto && elemento.open) elemento.close()
  }, [abierto])

  // El Escape del `<dialog>` cierra sin pasar por React: hay que sincronizarlo.
  useEffect(() => {
    const elemento = dialogo.current
    if (!elemento) return
    const alCerrar = () => onCerrar()
    elemento.addEventListener('close', alCerrar)
    return () => elemento.removeEventListener('close', alCerrar)
  }, [onCerrar])

  const anchos = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }

  return (
    <dialog
      ref={dialogo}
      onClick={(e) => {
        // Clic en el fondo, fuera de la tarjeta.
        if (e.target === dialogo.current) onCerrar()
      }}
      className={cx(
        'm-auto w-[calc(100vw-2rem)] rounded-lg border border-[var(--color-borde-fuerte)]',
        'bg-[var(--color-panel)] p-0 text-[var(--color-texto)] shadow-2xl',
        'backdrop:bg-black/45 backdrop:backdrop-blur-sm',
        anchos[ancho],
      )}
      aria-label={titulo}
    >
      <header className="flex items-start justify-between gap-3 border-b border-[var(--color-borde)] px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-[14px] font-semibold">{titulo}</h2>
          {descripcion && (
            <p className="mt-0.5 text-[12px] text-[var(--color-texto-suave)]">{descripcion}</p>
          )}
        </div>
        <button
          onClick={onCerrar}
          className="shrink-0 text-[var(--color-texto-tenue)] hover:text-[var(--color-texto)]"
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>
      </header>

      {children && (
        <div className="scroll-sutil max-h-[min(60vh,520px)] overflow-y-auto px-4 py-3">
          {children}
        </div>
      )}

      {pie && (
        <footer className="flex flex-wrap justify-end gap-2 border-t border-[var(--color-borde)] px-4 py-3">
          {pie}
        </footer>
      )}
    </dialog>
  )
}

export function Confirmacion({
  abierto,
  onCerrar,
  onConfirmar,
  titulo,
  descripcion,
  detalle,
  etiquetaConfirmar = 'Confirmar',
  peligro = false,
  cargando = false,
}: {
  abierto: boolean
  onCerrar: () => void
  onConfirmar: () => void
  titulo: string
  descripcion: string
  detalle?: ReactNode
  etiquetaConfirmar?: string
  peligro?: boolean
  cargando?: boolean
}) {
  return (
    <Dialogo
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={titulo}
      ancho="sm"
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar} disabled={cargando}>
            Cancelar
          </Boton>
          <Boton
            variante={peligro ? 'peligro' : 'primario'}
            onClick={onConfirmar}
            cargando={cargando}
          >
            {etiquetaConfirmar}
          </Boton>
        </>
      }
    >
      <div className="flex items-start gap-2.5">
        {peligro && (
          <AlertTriangle size={17} className="mt-0.5 shrink-0 text-[var(--color-opositora)]" />
        )}
        <div className="min-w-0">
          <p className="text-[13px]">{descripcion}</p>
          {detalle && <div className="mt-2">{detalle}</div>}
        </div>
      </div>
    </Dialogo>
  )
}
